///home/ec2-user/brand-insights-dashboard/frontend/src/components/Filters.js

// /home/ec2-user/brand-insights-dashboard/frontend/src/components/Filters.js
import React, { useState, useMemo, useCallback, memo } from 'react';

import {
  Button, TextField, Typography, Box,
  MenuItem, Menu, ListItemIcon, ListItemText,
  Checkbox, IconButton, Slider  // AGGIUNGI Slider qui
} from '@mui/material';

import {
  ArrowDropDown, ClearAll,
  Business, Smartphone, PhotoCamera,
  Search, TrendingUp
} from '@mui/icons-material';

import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns';
import { it } from 'date-fns/locale';

// ============================================
// 1. DATE RANGE PICKER (Nuovo componente) - MEMOIZED
// ============================================

export const DateRangePicker = memo(({ 
  value = { start: null, end: null }, 
  onChange,
  width = 300,
  loading = false,
  availableRange = null
}) => {
  // Prima dichiara TUTTI gli hook, poi le condizioni
  const minDate = useMemo(() => 
    availableRange?.min_date ? new Date(availableRange.min_date) : null, 
    [availableRange?.min_date]
  );
  
  const maxDate = useMemo(() => 
    availableRange?.max_date ? new Date(availableRange.max_date) : null, 
    [availableRange?.max_date]
  );
  
  const totalDays = useMemo(() => {
    if (!minDate || !maxDate) return 1;
    return Math.max(1, Math.ceil((maxDate - minDate) / (1000 * 60 * 60 * 24)));
  }, [minDate, maxDate]);
  
  const getSliderValue = useCallback(() => {
    if (!value.start || !value.end || !minDate) {
      return [0, 100];
    }
    
    const startDays = Math.max(0, Math.ceil((new Date(value.start) - minDate) / (1000 * 60 * 60 * 24)));
    const endDays = Math.min(totalDays, Math.ceil((new Date(value.end) - minDate) / (1000 * 60 * 60 * 24)));
    
    const startPercent = (startDays / totalDays) * 100;
    const endPercent = (endDays / totalDays) * 100;
    
    return [startPercent, endPercent];
  }, [value.start, value.end, minDate, totalDays]);

  const sliderToDate = useCallback((percent) => {
    if (!minDate) return new Date();
    const days = (percent / 100) * totalDays;
    const date = new Date(minDate);
    date.setDate(minDate.getDate() + Math.floor(days));
    return date;
  }, [minDate, totalDays]);

  const handleSliderChange = useCallback((event, newValue) => {
    const [startPercent, endPercent] = newValue;
    
    const startDate = sliderToDate(startPercent);
    const endDate = sliderToDate(endPercent);
    
    if (startDate <= endDate) {
      onChange({
        start: startDate,
        end: endDate
      });
    }
  }, [onChange, sliderToDate]);

  const formatDate = useCallback((date) => {
    if (!date) return '';
    const dateObj = new Date(date);
    return dateObj.toLocaleDateString('it-IT', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric'
    });
  }, []);

  const sliderValue = getSliderValue();

  // Solo DOPO gli hook, fai le condizioni di rendering
  if (!availableRange || !minDate || !maxDate) {
    return (
      <LocalizationProvider dateAdapter={AdapterDateFns} adapterLocale={it}>
        <Box sx={{ width: width, p: 1 }}>
          <Typography variant="caption" color="text.secondary">
            Caricamento date...
          </Typography>
        </Box>
      </LocalizationProvider>
    );
  }

  return (
    <LocalizationProvider dateAdapter={AdapterDateFns} adapterLocale={it}>
      <Box sx={{ 
        width: width,
        p: 0.5,
      }}>
        {/* Date range disponibile */}
        <Box sx={{ 
          display: 'flex', 
          justifyContent: 'center', 
          alignItems: 'center',
          mb: -1,
          gap: 0.5
        }}>
          <Typography variant="caption" color="text.secondary">
            {formatDate(minDate)}
          </Typography>
          <Typography variant="caption" color="text.secondary">
            →
          </Typography>
          <Typography variant="caption" color="text.secondary">
            {formatDate(maxDate)}
          </Typography>
        </Box>
        
        {/* Slider */}
        <Slider
          value={sliderValue}
          onChange={handleSliderChange}
          valueLabelDisplay="auto"
          valueLabelFormat={(value) => formatDate(sliderToDate(value))}
          min={0}
          max={100}
          disabled={loading}
          size="small"
          sx={{
            color: 'primary.main',
            height: 2,
            '& .MuiSlider-thumb': {
              width: 12,
              height: 12,
              '&:hover, &.Mui-focusVisible': {
                boxShadow: '0px 0px 0px 4px rgba(25, 118, 210, 0.16)',
              },
            },
            '& .MuiSlider-track': {
              border: 'none',
              height: 2,
            },
            '& .MuiSlider-rail': {
              opacity: 0.2,
              backgroundColor: '#aaa',
              height: 2,
            },
          }}
        />
      </Box>
    </LocalizationProvider>
  );
});

// ============================================
// 2. SEARCHABLE MULTI-SELECT (per Brand) - MEMOIZED
// ============================================

export const SearchableMultiSelect = memo(({
  label,
  value = [],
  onChange,
  options = [],
  icon: Icon,
  width = 200,
  loading = false,
  ...props
}) => {
  const [anchorEl, setAnchorEl] = useState(null);
  const [searchQuery, setSearchQuery] = useState('');
  const open = Boolean(anchorEl);

  const handleClick = useCallback((event) => {
    setAnchorEl(event.currentTarget);
    setSearchQuery('');
  }, []);

  const handleClose = useCallback(() => {
    setAnchorEl(null);
    setSearchQuery('');
  }, []);

  const filteredOptions = useMemo(() => {
    if (!searchQuery.trim()) return options;
    
    const query = searchQuery.toLowerCase().trim();
    return options.filter(opt => 
      opt.label.toLowerCase().includes(query) ||
      opt.value.toLowerCase().includes(query)
    );
  }, [options, searchQuery]);

  const toggleSelection = useCallback((optionValue) => {
    if (value.includes(optionValue)) {
      onChange(value.filter(v => v !== optionValue));
    } else {
      onChange([...value, optionValue]);
    }
  }, [value, onChange]);

  const selectAllFiltered = useCallback(() => {
    const newValues = [...value];
    filteredOptions.forEach(option => {
      if (!newValues.includes(option.value)) {
        newValues.push(option.value);
      }
    });
    onChange(newValues);
  }, [value, onChange, filteredOptions]);

  const deselectAllFiltered = useCallback(() => {
    const newValues = value.filter(val => 
      !filteredOptions.some(opt => opt.value === val)
    );
    onChange(newValues);
  }, [value, onChange, filteredOptions]);

  const selectedCount = value.length;
  const filteredCount = filteredOptions.length;
  const selectedInFiltered = filteredOptions.filter(opt => 
    value.includes(opt.value)
  ).length;

  const isAllFilteredSelected = filteredCount > 0 && 
    selectedInFiltered === filteredCount;

  return (
    <>
      <Button
        variant="outlined"
        size="small"
        onClick={handleClick}
        startIcon={Icon ? <Icon /> : null}
        endIcon={<ArrowDropDown />}
        sx={{
          minWidth: width,
          justifyContent: 'space-between',
          textTransform: 'none',
          borderColor: value.length > 0 ? 'primary.main' : 'grey.400',
          bgcolor: value.length > 0 ? 'primary.50' : 'transparent',
          '&:hover': {
            borderColor: 'primary.main',
            bgcolor: value.length > 0 ? 'primary.100' : 'action.hover'
          }
        }}
        disabled={loading}
        {...props}
      >
        <Box sx={{ 
          display: 'flex', 
          alignItems: 'center',
          overflow: 'hidden',
          flexGrow: 1
        }}>
          <Typography variant="body2" noWrap>
            {value.length > 0 
              ? `${label}: ${value.length}`
              : label
            }
          </Typography>
        </Box>
      </Button>

      <Menu
        anchorEl={anchorEl}
        open={open}
        onClose={handleClose}
        PaperProps={{
          sx: { 
            width: width + 40,
            maxHeight: 500,
            display: 'flex',
            flexDirection: 'column'
          }
        }}
      >
        {/* Search Bar */}
        <Box sx={{ p: 1.5, borderBottom: 1, borderColor: 'divider' }}>
          <TextField
            fullWidth
            size="small"
            placeholder={`Cerca ${label.toLowerCase()}...`}
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            autoFocus
            InputProps={{
              startAdornment: (
                <Search fontSize="small" sx={{ mr: 1, color: 'action.active' }} />
              ),
              endAdornment: searchQuery && (
                <IconButton
                  size="small"
                  onClick={() => setSearchQuery('')}
                  sx={{ p: 0.5 }}
                >
                  <ClearAll fontSize="small" />
                </IconButton>
              )
            }}
            sx={{ 
              '& .MuiOutlinedInput-root': { 
                height: 36,
                fontSize: '0.875rem'
              }
            }}
          />
          
          {/* Counters and Select All */}
          {searchQuery && (
            <Box sx={{ 
              display: 'flex', 
              justifyContent: 'space-between', 
              alignItems: 'center',
              mt: 1,
              px: 0.5
            }}>
              <Typography variant="caption" color="text.secondary">
                {filteredCount} risultati • {selectedInFiltered} selezionati
              </Typography>
              
              <Button
                size="small"
                onClick={isAllFilteredSelected ? deselectAllFiltered : selectAllFiltered}
                sx={{ fontSize: '0.75rem', py: 0.25 }}
              >
                {isAllFilteredSelected ? 'Deseleziona tutti' : 'Seleziona tutti'}
              </Button>
            </Box>
          )}
        </Box>

        {/* Results List */}
        <Box sx={{ 
          flex: 1, 
          overflow: 'auto',
          maxHeight: 350 
        }}>
          {filteredOptions.length === 0 ? (
            <Box sx={{ p: 2, textAlign: 'center' }}>
              <Typography variant="body2" color="text.secondary">
                {searchQuery 
                  ? `Nessun risultato per "${searchQuery}"`
                  : 'Nessuna opzione disponibile'
                }
              </Typography>
            </Box>
          ) : (
            filteredOptions.map((option) => (
              <MenuItem 
                key={option.value} 
                onClick={() => toggleSelection(option.value)}
                selected={value.includes(option.value)}
                sx={{ 
                  bgcolor: value.includes(option.value) ? 'action.selected' : 'transparent',
                  py: 0.75
                }}
              >
                <Checkbox 
                  checked={value.includes(option.value)} 
                  size="small" 
                />
                <ListItemText 
                  primary={option.label} 
                  primaryTypographyProps={{ fontSize: '0.875rem' }}
                />
              </MenuItem>
            ))
          )}
        </Box>

        {/* Footer */}
        <Box sx={{ 
          p: 1, 
          borderTop: 1, 
          borderColor: 'divider',
          bgcolor: 'grey.50'
        }}>
          <Typography variant="caption" color="text.secondary">
            {selectedCount} di {options.length} selezionati
          </Typography>
        </Box>
      </Menu>
    </>
  );
});

// ============================================
// 3. MULTI-SELECT (per Platform, Post Type) - MEMOIZED
// ============================================

export const MultiSelect = memo(({
  label,
  value = [],
  onChange,
  options = [],
  icon: Icon,
  width = 180,
  loading = false,
  ...props
}) => {
  const [anchorEl, setAnchorEl] = useState(null);
  const open = Boolean(anchorEl);

  const handleClick = useCallback((event) => {
    setAnchorEl(event.currentTarget);
  }, []);

  const handleClose = useCallback(() => {
    setAnchorEl(null);
  }, []);

  const toggleSelection = useCallback((optionValue) => {
    if (value.includes(optionValue)) {
      onChange(value.filter(v => v !== optionValue));
    } else {
      onChange([...value, optionValue]);
    }
  }, [value, onChange]);

  return (
    <>
      <Button
        variant="outlined"
        size="small"
        onClick={handleClick}
        startIcon={Icon ? <Icon /> : null}
        endIcon={<ArrowDropDown />}
        sx={{
          minWidth: width,
          justifyContent: 'space-between',
          textTransform: 'none',
          borderColor: value.length > 0 ? 'primary.main' : 'grey.400',
          bgcolor: value.length > 0 ? 'primary.50' : 'transparent',
          '&:hover': {
            borderColor: 'primary.main',
            bgcolor: value.length > 0 ? 'primary.100' : 'action.hover'
          }
        }}
        disabled={loading}
        {...props}
      >
        <Box sx={{ 
          display: 'flex', 
          alignItems: 'center',
          overflow: 'hidden',
          flexGrow: 1
        }}>
          <Typography variant="body2" noWrap>
            {value.length > 0 
              ? `${label}: ${value.length}`
              : label
            }
          </Typography>
        </Box>
      </Button>

      <Menu
        anchorEl={anchorEl}
        open={open}
        onClose={handleClose}
        PaperProps={{
          sx: { 
            width: width + 20,
            maxHeight: 400 
          }
        }}
      >
        {options.map((option) => (
          <MenuItem 
            key={option.value} 
            onClick={() => toggleSelection(option.value)}
            selected={value.includes(option.value)}
            sx={{ 
              bgcolor: value.includes(option.value) ? 'action.selected' : 'transparent'
            }}
          >
            <Checkbox checked={value.includes(option.value)} />
            <ListItemText primary={option.label} />
          </MenuItem>
        ))}
      </Menu>
    </>
  );
});

// ============================================
// 4. FILTER DROPDOWN (per Client, Project, KPI) - MEMOIZED
// ============================================

export const FilterDropdown = memo(({ 
  label, 
  value, 
  onChange, 
  options = [], 
  icon: Icon,
  width = 180,
  showAllOption = true,
  loading = false,
  ...props 
}) => {
  const [anchorEl, setAnchorEl] = useState(null);
  const open = Boolean(anchorEl);

  const handleClick = useCallback((event) => {
    setAnchorEl(event.currentTarget);
  }, []);

  const handleClose = useCallback((selectedValue) => {
    if (selectedValue !== undefined) {
      onChange(selectedValue);
    }
    setAnchorEl(null);
  }, [onChange]);

  const selectedOption = options.find(opt => opt.value === value);

  return (
    <>
      <Button
        variant="outlined"
        size="small"
        onClick={handleClick}
        startIcon={Icon ? <Icon /> : null}
        endIcon={<ArrowDropDown />}
        sx={{
          minWidth: width,
          justifyContent: 'space-between',
          textTransform: 'none',
          borderColor: value ? 'primary.main' : 'grey.400',
          bgcolor: value ? 'primary.50' : 'transparent',
          '&:hover': {
            borderColor: 'primary.main',
            bgcolor: value ? 'primary.100' : 'action.hover'
          }
        }}
        disabled={loading}
        {...props}
      >
        <Box sx={{ 
          display: 'flex', 
          alignItems: 'center',
          overflow: 'hidden',
          flexGrow: 1
        }}>
          {selectedOption?.icon && (
            <Box sx={{ mr: 1, display: 'flex', alignItems: 'center' }}>
              {selectedOption.icon}
            </Box>
          )}
          <Typography variant="body2" noWrap>
            {selectedOption ? selectedOption.label : label}
          </Typography>
        </Box>
      </Button>

      <Menu
        anchorEl={anchorEl}
        open={open}
        onClose={() => handleClose()}
        PaperProps={{
          sx: { 
            width: width + 20,
            maxHeight: 400 
          }
        }}
      >
        {showAllOption && (
          <MenuItem onClick={() => handleClose('')}>
            <ListItemText primary="Tutti" />
          </MenuItem>
        )}
        
        {options.map((option) => (
          <MenuItem 
            key={option.value} 
            onClick={() => handleClose(option.value)}
            selected={value === option.value}
            sx={{ 
              bgcolor: value === option.value ? 'action.selected' : 'transparent'
            }}
          >
            {option.icon && (
              <ListItemIcon sx={{ minWidth: 36 }}>
                {option.icon}
              </ListItemIcon>
            )}
            <ListItemText primary={option.label} />
          </MenuItem>
        ))}
      </Menu>
    </>
  );
});

// ============================================
// 5. KPI SELECTOR (Nuovo componente) - MEMOIZED
// ============================================

export const KpiSelector = memo(({ 
  value = 'total_interactions', 
  onChange,
  width = 220,
  loading = false 
}) => {
  const kpiOptions = useMemo(() => [
    { value: 'total_posts', label: 'Numero Post' },               // <-- AGGIUNTO "Numero Post"
    { value: 'total_interactions', label: 'Total Engagement' },
    { value: 'total_likes', label: 'Likes' },
    { value: 'total_comments', label: 'Comments' },
    { value: 'total_shares', label: 'Shares' },
    { value: 'total_views', label: 'Views' },
    { value: 'engagement_rate', label: 'Engagement Rate %' }
  ], []);
  
  return (
    <FilterDropdown
      label="KPI"
      value={value}
      onChange={onChange}
      options={kpiOptions}
      icon={TrendingUp}
      width={width}
      loading={loading}
      showAllOption={false}
    />
  );
});

// ============================================
// 6. FILTERS CONTAINER (Layout principale - OTTIMIZZATO)
// ============================================

export const FiltersContainer = memo(({ 
  filters, 
  onFilterChange,
  onSingleFilterChange,
  filterOptions = {},
  loading = false,
  hideKpi = false  // <-- NUOVA PROP: nasconde il selettore KPI
}) => {
  // DEBUG (commentato)
  // console.log('🔧 FiltersContainer rendering');
  
  // Handler per filtri multi
  const handleMultiFilterChange = useCallback((key, values) => {
    onFilterChange({ ...filters, [key]: values });
  }, [filters, onFilterChange]);

  // Handler per filtri single
  const handleSingleFilterChangeCallback = useCallback((key, value) => {
    if (onSingleFilterChange) {
      onSingleFilterChange(key, value);
    } else {
      onFilterChange({ ...filters, [key]: value });
    }
  }, [filters, onFilterChange, onSingleFilterChange]);

  // Handler per date range
  const handleDateRangeChange = useCallback((range) => {
    onFilterChange({ ...filters, posted_at_range: range });
  }, [filters, onFilterChange]);

  // Memoizza valori
  const memoizedDateRange = useMemo(() => 
    filters.posted_at_range || { start: null, end: null }, 
    [filters.posted_at_range]
  );

  const memoizedBrands = useMemo(() => 
    filterOptions.brands || [], 
    [filterOptions.brands]
  );

  const memoizedPlatforms = useMemo(() => 
    filterOptions.platforms || [], 
    [filterOptions.platforms]
  );

  const memoizedPostTypes = useMemo(() => 
    filterOptions.post_types || [], 
    [filterOptions.post_types]
  );

  const memoizedBrandValue = useMemo(() => filters.brand || [], [filters.brand]);
  const memoizedPlatformValue = useMemo(() => filters.platform || [], [filters.platform]);
  const memoizedPostTypeValue = useMemo(() => filters.post_type || [], [filters.post_type]);
  const memoizedKpiValue = useMemo(() => filters.selectedKpi || 'total_interactions', [filters.selectedKpi]);

  return (
    <>
      {/* Barra filtri orizzontale */}
      <Box sx={{ 
        display: 'flex',
        alignItems: 'center',
        gap: 2,
        flexWrap: 'wrap'
      }}>
        {/* Slider Date Range Picker */}
        <DateRangePicker
          value={memoizedDateRange}
          onChange={handleDateRangeChange}
          width={280}
          loading={loading}
          availableRange={filterOptions.date_range || null}
        />

        {/* Brand Filter */}
        <SearchableMultiSelect
          label="Brand"
          value={memoizedBrandValue}
          onChange={(values) => handleMultiFilterChange('brand', values)}
          options={memoizedBrands}
          icon={Business}
          width={200}
          loading={loading}
        />

        {/* Platform Filter */}
        <MultiSelect
          label="Platform"
          value={memoizedPlatformValue}
          onChange={(values) => handleMultiFilterChange('platform', values)}
          options={memoizedPlatforms}
          icon={Smartphone}
          width={180}
          loading={loading}
        />

        {/* Post Type Filter */}
        <MultiSelect
          label="Post Type"
          value={memoizedPostTypeValue}
          onChange={(values) => handleMultiFilterChange('post_type', values)}
          options={memoizedPostTypes}
          icon={PhotoCamera}
          width={180}
          loading={loading}
        />

        {/* KPI Selector - VISIBILE SOLO SE hideKpi è false */}
        {!hideKpi && (
          <KpiSelector
            value={memoizedKpiValue}
            onChange={(value) => handleSingleFilterChangeCallback('selectedKpi', value)}
            loading={loading}
          />
        )}
      </Box>
    </>
  );
});

// Aggiungi displayName
FiltersContainer.displayName = 'FiltersContainer';
DateRangePicker.displayName = 'DateRangePicker';
SearchableMultiSelect.displayName = 'SearchableMultiSelect';
MultiSelect.displayName = 'MultiSelect';
FilterDropdown.displayName = 'FilterDropdown';
KpiSelector.displayName = 'KpiSelector';

export default FiltersContainer;